import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
 ShoppingBag,
 Check,
 Shield,
 Lock,
 RefreshCw,
 CreditCard,
 Mail,
 X,
 Receipt,
 Megaphone,
 ChevronDown,
 Info
} from 'lucide-react';
import {
 getFilteredProductCards,
 getCategoryTheme
} from '../config/productUtils';
import { getProductMainImage } from '../config/productImages';
import LoginModal from '../components/LoginModal';
import { submitGuestOrder, checkOrderStatus } from '../api/productApi';
import { autoLoginWithEmail } from '../store/userSlice';

const AI_FLEET_PRODUCT_TYPES = [
 'aimonkey_vip',
 'fleet_gpt_plus',
 'fleet_claude',
 'fleet_grok',
 'fleet_gemini'
];

const PRODUCT_TABS = [
 {
 id: 'vip',
 label: 'VIP大会员车队',
 productTypes: ['aimonkey_vip']
 },
 {
 id: 'gpt',
 label: 'GPT车队',
 productTypes: ['fleet_gpt_plus']
 },
 {
 id: 'grok',
 label: 'Grok车队',
 productTypes: ['fleet_grok']
 },
 {
 id: 'claude',
 label: 'Claude车队',
 productTypes: ['fleet_claude']
 },
 {
 id: 'gemini',
 label: 'Gemini车队',
 productTypes: ['fleet_gemini']
 }
];

const PRODUCT_CARD_DESCRIPTIONS = {
 aimonkey_vip: '购买后可使用 AI车队中的 GPT Plus/Business号、Claude Max号、Gemini Pro号和 Grok Super号。',
 fleet_gpt_plus: '购买后可使用 AI车队中的 GPT Plus/Business号。',
 fleet_claude: '适合长文写作、总结归纳、翻译润色和代码理解场景。',
 fleet_grok: '购买后可使用 AI车队中的 Grok Super号。',
 fleet_gemini: '购买后可使用 AI车队中的 Gemini Pro号。'
};

const PRODUCT_SALE_NOTICES = {
 claude: {
 name: 'Claude',
 reason: '由于 Claude 当前存在并发限制，且封号后的售后补偿已难以维持固定退款，整体成本持续上升。'
 },
 grok: {
 name: 'Grok',
 reason: '由于 Grok 服务的账号及维护成本持续上升，为保障现有用户的使用体验，现已不再单独出售。'
 },
 gemini: {
 name: 'Gemini',
 reason: '由于 Gemini 服务的账号及维护成本持续上升，为保障现有用户的使用体验，现已不再单独出售。'
 }
};

const USD_TO_RMB_EXCHANGE_RATE = 7.2;

const getProductRmbPrice = (product) => {
 const configuredRmbPrice = Number(product?.priceRMB);
 if (Number.isFinite(configuredRmbPrice)) {
 return configuredRmbPrice;
 }

 return Number(product?.price || 0) * USD_TO_RMB_EXCHANGE_RATE;
};

const formatPrice = (price) => Number(price || 0).toFixed(1);

const PERIOD_TABS = [
 {
 id: 'all',
 label: '全部',
 description: '默认展示全部周期，可按需筛选合适的套餐',
 days: null
 },
 {
 id: 'monthly',
 label: '月度',
 description: '适合短期使用，灵活续费',
 days: 30
 },
 {
 id: 'quarterly',
 label: '季度',
 description: '更适合持续使用，价格与灵活性更均衡',
 days: 90
 },
 {
 id: 'yearly',
 label: '年度',
 description: '享受完整12个月服务，仅收取10个月费用，相当于免费赠送2个月',
 days: 365,
 promoLabel: '付10享12'
 }
];

const isProductInPeriod = (product, periodId) => {
 if (periodId === 'all') return true;
 if (periodId === 'monthly') return product.month === 1;
 if (periodId === 'quarterly') return product.month === 3;
 if (periodId === 'yearly') return product.month === 12 || product.month > 3;
 return false;
};

const isProductInTab = (product, tabId) => {
 const currentTab = PRODUCT_TABS.find((tab) => tab.id === tabId);
 if (!currentTab) return false;
 return currentTab.productTypes.includes(product.productCategoryId);
};

const getProductCardDescription = (product) => {
 return product.productDescription || PRODUCT_CARD_DESCRIPTIONS[product.productCategoryId] || '查看该套餐详情并选择适合您的使用周期。';
};

const ShopCheckout = () => {
 const navigate = useNavigate();
 const dispatch = useDispatch();

 // 获取登录状态和用户信息
 const { isLoggedIn, user } = useSelector((state) => state.user);

 // 状态管理
 const [selectedProduct, setSelectedProduct] = useState(null);
 const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('alipay');
 const [customerEmail, setCustomerEmail] = useState('');
 const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
 const [paymentLoading, setPaymentLoading] = useState(false);
 const [paymentUrl, setPaymentUrl] = useState('');
 const [showManualRedirect, setShowManualRedirect] = useState(false);
 const [orderNumber, setOrderNumber] = useState('');
 const [isPolling, setIsPolling] = useState(false);
 const pollingIntervalRef = useRef(null);
 const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
 const [showPaymentMethodAlert, setShowPaymentMethodAlert] = useState(false);
 const [emailError, setEmailError] = useState('');

 // 周期Tab状态
 const [selectedSubTab, setSelectedSubTab] = useState('all'); // 全部/月度/季度/年度
 const [selectedProductTab, setSelectedProductTab] = useState('vip');

 // 获取所有产品（memoize 避免每次渲染创建新引用导致下游 useMemo 失效）
 const allProducts = useMemo(() => getFilteredProductCards('all'), []);

 const eligibleAiFleetProducts = useMemo(() => {
 // 固定筛选 AI 车队服务
 const filteredProducts = allProducts.filter(p =>
 AI_FLEET_PRODUCT_TYPES.includes(p.productCategoryId)
 );

 // 产品排序：VIP → GPT → Grok → Claude → Gemini
 const sortOrder = {
 'fleet_gpt_plus': 1,
 'fleet_grok': 2,
 'fleet_claude': 3,
 'fleet_gemini': 4,
 'aimonkey_vip': 0 // VIP排最前
 };

 filteredProducts.sort((a, b) => {
 const orderA = sortOrder[a.productCategoryId] ?? 999;
 const orderB = sortOrder[b.productCategoryId] ?? 999;
 if (orderA !== orderB) return orderA - orderB;
 // 同类产品按月份降序排列（年卡→季卡→月卡）
 return (b.month || 0) - (a.month || 0);
 });

 return filteredProducts;
 }, [allProducts]);

 const periodProducts = useMemo(() => {
 return eligibleAiFleetProducts.filter(product => isProductInPeriod(product, selectedSubTab));
 }, [eligibleAiFleetProducts, selectedSubTab]);

 // 获取当前应该显示的产品
 const currentProducts = useMemo(() => {
 if (PRODUCT_SALE_NOTICES[selectedProductTab]) {
 return [];
 }
 return periodProducts.filter(product => isProductInTab(product, selectedProductTab));
 }, [periodProducts, selectedProductTab]);

 const activePeriodTab = PERIOD_TABS.find(tab => tab.id === selectedSubTab) || PERIOD_TABS[0];

 const gptPricingHint = useMemo(() => {
 const gptQuarterlyProduct = eligibleAiFleetProducts.find((product) => (
 product.productCategoryId === 'fleet_gpt_plus' && product.month === 3
 ));
 const gptYearlyProduct = eligibleAiFleetProducts.find((product) => (
 product.productCategoryId === 'fleet_gpt_plus' && product.month === 12
 ));

 if (!gptQuarterlyProduct || !gptYearlyProduct) {
 return '';
 }

 const quarterlyPerDay = (getProductRmbPrice(gptQuarterlyProduct) / 90).toFixed(1);
 const yearlyPerDay = (getProductRmbPrice(gptYearlyProduct) / 365).toFixed(1);

 return `GPT 季卡、年卡按人民币套餐价估算，折合约 ¥${quarterlyPerDay}/天、¥${yearlyPerDay}/天，长期使用低至每天约 1 元。`;
 }, [eligibleAiFleetProducts]);

 const activeSaleNotice = PRODUCT_SALE_NOTICES[selectedProductTab] || null;

 // 当前周期下如果所选产品tab无商品，则自动切换到第一个可用tab
 useEffect(() => {
 if (PRODUCT_SALE_NOTICES[selectedProductTab]) {
 return;
 }

 const hasProductsInCurrentTab = periodProducts.some(product => isProductInTab(product, selectedProductTab));

 if (hasProductsInCurrentTab) {
 return;
 }

 const firstAvailableTab = PRODUCT_TABS.find(tab =>
 periodProducts.some(product => tab.productTypes.includes(product.productCategoryId))
 );

 if (firstAvailableTab && firstAvailableTab.id !== selectedProductTab) {
 setSelectedProductTab(firstAvailableTab.id);
 }
 }, [periodProducts, selectedProductTab]);

 // 默认选择当前周期下的第一个产品
 useEffect(() => {
 setSelectedProduct((prevSelectedProduct) => {
 if (currentProducts.length === 0) {
 return null;
 }

 const isCurrentSelectionVisible = prevSelectedProduct
 ? currentProducts.some(product => product.id === prevSelectedProduct.id)
 : false;

 if (isCurrentSelectionVisible) {
 return prevSelectedProduct;
 }

 return currentProducts[0];
 });
 }, [currentProducts]);

 // 支付方式配置
 const paymentMethods = {
 all: [
 { id: 'alipay', name: '支付宝', icon: '/images/payment/16.jpg', description: '国内用户首选', channel: 1 },
 { id: 'alipay_hk', name: '支付宝香港', icon: '/images/payment/17.jpg', description: '支持国际支付', channel: 2 },
 { id: 'credit_card', name: '信用卡', icon: '/images/payment/18.jpg', description: 'Visa/Mastercard', channel: 10 }
 ]
 };

 // 用户登录后自动填充邮箱
 useEffect(() => {
 if (isLoggedIn && user) {
 const email = user.uid || user.email || '';
 setCustomerEmail(email);
 }
 }, [isLoggedIn, user]);

 // 收货信息验证（支持邮箱或手机号，只验证非空）
 const validateCustomerInfo = (info) => {
 return info && info.trim().length > 0;
 };

 // 处理产品选择（支持取消选择）
 const handleSelectProduct = (product) => {
 // 如果点击的是已选中的产品，则取消选择
 if (selectedProduct?.id === product.id) {
 setSelectedProduct(null);
 } else {
 setSelectedProduct(product);
 }

 // 滚动到支付面板（移动端）
 if (window.innerWidth < 1024) {
 setTimeout(() => {
 document.getElementById('checkout-panel')?.scrollIntoView({ behavior: 'smooth' });
 }, 100);
 }
 };

 // 处理支付
 const handlePayment = async () => {
 // 验证产品选择
 if (!selectedProduct) {
 alert('请先选择产品');
 return;
 }

 // 验证收货信息（可以是邮箱或手机号）
 if (!customerEmail) {
 setEmailError('请输入收货信息（邮箱或手机号）');
 return;
 }
 if (!validateCustomerInfo(customerEmail)) {
 setEmailError('收货信息不能为空');
 return;
 }
 setEmailError('');

 // 验证支付方式
 if (!selectedPaymentMethod) {
 setShowPaymentMethodAlert(true);
 setTimeout(() => setShowPaymentMethodAlert(false), 3000);
 return;
 }

 setPaymentLoading(true);

 try {
 // 获取选中的支付方式详情
 const selectedPaymentDetails = paymentMethods.all.find(
 method => method.id === selectedPaymentMethod
 );

 if (!selectedPaymentDetails) {
 throw new Error('未找到支付方式');
 }

 // 准备支付请求数据
 const paymentData = {
 uid: customerEmail,
 product_id: selectedProduct.id,
 channel: selectedPaymentDetails.channel,
 domain: window.location.hostname,
 amount: selectedProduct.price,
 quantity: 1,
 version: selectedProduct.label || selectedProduct.key,
 productName: selectedProduct.productName,
 productType: selectedProduct.productCategoryId
 };

 console.log('支付请求数据:', paymentData);

 // 调用支付接口
 const response = await submitGuestOrder(paymentData);

 console.log('支付API返回:', response);

 if (response && response.data) {
 const { payUrl, out_trade_no } = response.data;

 if (payUrl) {
 setPaymentUrl(payUrl);
 setOrderNumber(out_trade_no);

 // 打开支付页面
 window.open(payUrl, '_blank');

 // 显示手动跳转按钮
 setShowManualRedirect(true);
 } else {
 throw new Error('支付链接为空');
 }
 } else {
 throw new Error('支付接口返回数据异常');
 }

 } catch (error) {
 console.error('支付失败:', error);
 alert(`支付失败: ${error.message}`);
 } finally {
 setPaymentLoading(false);
 }
 };

 // 订单状态轮询
 const startPolling = async (orderNo) => {
 setIsPolling(true);
 console.log('开始轮询订单状态，订单号:', orderNo);

 try {
 const response = await checkOrderStatus(orderNo);
 console.log('订单状态查询结果:', response);

 if (response && response.data) {
 const { pay_result } = response.data;

 if (pay_result === 'success') {
 // 支付成功
 console.log('🎉 支付完成！');
 setIsPolling(false);
 clearTimeout(pollingIntervalRef.current);

 // 如果用户未登录且有邮箱，先进行自动登录
 if (!isLoggedIn && !autoLoginAttempted && customerEmail) {
 console.log('💡 检测到未登录用户，使用订单邮箱进行自动登录:', customerEmail);
 setAutoLoginAttempted(true);

 try {
 const result = await dispatch(autoLoginWithEmail(customerEmail));

 if (autoLoginWithEmail.fulfilled.match(result)) {
 console.log('✅ 自动登录成功:', result.payload);
 setTimeout(() => {
 navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
 }, 500);
 } else {
 console.log('❌ 自动登录失败，直接跳转');
 navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
 }
 } catch (error) {
 console.error('💥 自动登录过程中发生错误，直接跳转:', error);
 navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
 }
 } else {
 console.log('🔄 用户已登录或已尝试过自动登录，直接跳转到成功页面');
 navigate(`/detail/success?orderId=${orderNo}&productType=${selectedProduct.productCategoryId}&productName=${encodeURIComponent(selectedProduct.productName)}&price=${selectedProduct.price}&version=${encodeURIComponent(selectedProduct.label || selectedProduct.key)}`);
 }
 return;
 } else {
 // 支付未完成，继续轮询
 console.log('支付未完成，继续轮询。当前状态:', pay_result);
 pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
 }
 } else {
 console.log('订单状态查询返回数据异常，继续轮询');
 pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
 }
 } catch (error) {
 console.error('支付状态轮询失败:', error);
 pollingIntervalRef.current = setTimeout(() => startPolling(orderNo), 5000);
 }
 };

 // 当有订单号和支付链接时启动轮询
 useEffect(() => {
 if (orderNumber && paymentUrl && !isPolling) {
 console.log('启动订单状态轮询');
 startPolling(orderNumber);
 }

 return () => {
 if (pollingIntervalRef.current) {
 clearTimeout(pollingIntervalRef.current);
 }
 };
 }, [orderNumber, paymentUrl]);

 // 组件卸载时清理定时器
 useEffect(() => {
 return () => {
 if (pollingIntervalRef.current) {
 clearTimeout(pollingIntervalRef.current);
 }
 };
 }, []);

 return (
 <div className="min-h-screen bg-cream-50">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
 <section className="mb-8 space-y-3" aria-label="购买公告">
 <details className="group overflow-hidden rounded-lg border border-amber-200 bg-white shadow-sm">
 <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 bg-amber-50 px-4 py-4 sm:flex-nowrap sm:px-5">
 <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
 <Megaphone className="h-5 w-5" aria-hidden="true" />
 </div>
 <div className="min-w-0 flex-1">
 <h2 className="text-base font-bold text-ink-900">价格调整公告</h2>
 <p className="mt-0.5 text-xs leading-5 text-ink-600">
 本次调整：VIP大会员涨价 10 元，GPT会员涨价 7 元
 </p>
 </div>
 <div className="ml-[52px] flex items-center gap-3 sm:ml-auto">
 <span className="rounded-full bg-red-500 px-2.5 py-1 text-xs font-bold text-white">年度付10享12</span>
 <span className="text-sm font-semibold text-ink-700">查看详情</span>
 <ChevronDown className="h-4 w-4 text-ink-500 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
 </div>
 </summary>

 <div className="border-t border-amber-200 px-5 py-5 sm:px-6">
 <p className="text-sm leading-6 text-ink-700">
 本站长期坚持低价策略，致力于<strong className="font-bold text-accent-600">普惠 AI</strong>，让更多用户以更低成本享受优质服务。但近一个季度，<strong className="font-bold text-amber-700">GPT 与 Claude 账号频繁封禁</strong>，<strong className="font-bold text-red-600">上游低价活动取消，封号后无法退款</strong>，车队运营成本大幅上涨。为保障服务稳定，本站不得不调整 VIP大会员车队与 GPT车队价格，感谢您的理解。
 </p>
 <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-ink-200 pt-4 text-sm text-ink-700">
 <span className="font-semibold">年度套餐继续提供完整 12 个月服务</span>
 <span className="font-bold text-red-600">付 10 个月，享 12 个月</span>
 </div>
 </div>
 </details>

 <details className="group overflow-hidden rounded-lg border border-sky-200 bg-white shadow-sm">
 <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 bg-sky-50 px-4 py-4 sm:flex-nowrap sm:px-5">
 <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white">
 <Info className="h-5 w-5" aria-hidden="true" />
 </div>
 <div className="min-w-0 flex-1">
 <h2 className="text-base font-bold text-ink-900">车队产品说明与频率限制</h2>
 <p className="mt-0.5 text-xs leading-5 text-ink-600">
 共享 AI 网页账户服务，以更低价格满足日常使用需求；点击查看各产品使用限制。
 </p>
 </div>
 <div className="ml-[52px] flex items-center gap-3 sm:ml-auto">
 <span className="text-sm font-semibold text-ink-700">查看详情</span>
 <ChevronDown className="h-4 w-4 text-ink-500 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
 </div>
 </summary>

 <div className="border-t border-sky-200 px-5 py-5 sm:px-6">
 <div className="space-y-3 text-sm leading-6 text-ink-700">
 <p>
 <strong className="font-bold text-ink-900">什么是 AI 车队？</strong>车队产品是由平台统一维护的 AI 账户网页共享服务，仅支持在网页端使用。多个用户共享同一组 AI 账户资源，因此能够在保障日常使用体验的前提下，把价格降到比官方独享账户更低的水平。
 </p>
 <p>
 您无需使用或维护自己的官方账号，账号的日常维护与异常处理由平台负责。车队产品适合重视性价比、以日常问答、写作和辅助工作为主的用户。
 </p>
 <p>
 <strong className="font-bold text-amber-700">请在购买前了解：</strong>共享服务的使用体验、稳定性与可用额度不等同于官方独享账户。独享账户有更完整的使用体验和更高的资源优先级；车队则以更低价格提供够用的网页端服务，价格与体验相对应。
 </p>
 </div>

 <div className="mt-5 overflow-hidden rounded-lg border border-sky-200">
 <div className="bg-sky-100 px-4 py-3 text-sm font-bold text-ink-900">各产品频率限制</div>
 <dl className="divide-y divide-sky-100 text-sm text-ink-700">
 <div className="grid gap-1 px-4 py-3 sm:grid-cols-[8rem_1fr] sm:gap-4">
 <dt className="font-semibold text-ink-900">GPT</dt>
 <dd>Auto 模式每小时 60 次；Thinking 模式每小时 60 次。</dd>
 </div>
 <div className="grid gap-1 px-4 py-3 sm:grid-cols-[8rem_1fr] sm:gap-4">
 <dt className="font-semibold text-ink-900">Claude</dt>
 <dd>3小时内最多 5 次；Sonnet 每3小时 40 次，或 Sonnet 每3小时 20 次加 Opus 每3小时 20 次，具体以当前账号开放的模型组合为准。</dd>
 </div>
 <div className="grid gap-1 px-4 py-3 sm:grid-cols-[8rem_1fr] sm:gap-4">
 <dt className="font-semibold text-ink-900">Grok</dt>
 <dd>每 2 小时 50 次。</dd>
 </div>
 <div className="grid gap-1 px-4 py-3 sm:grid-cols-[8rem_1fr] sm:gap-4">
 <dt className="font-semibold text-ink-900">Gemini</dt>
 <dd>每小时 30 次。</dd>
 </div>
 </dl>
 </div>
 <p className="mt-3 text-xs leading-5 text-ink-500">以上为当前车队服务的使用限制；AI 官方策略或账号资源调整时，平台将以实际可用情况为准。</p>
 </div>
 </details>
 </section>

 <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

 {/* 左侧 - 产品列表 (占3/5) */}
 <div className="lg:col-span-3">

 {/* 产品类型 Tab */}
 <div className="mb-8">
 <h3 className="text-base font-bold text-ink-900 mb-3">
 选择产品
 </h3>
 <div className="flex gap-2 overflow-x-auto pb-1">
 {PRODUCT_TABS.map((tab) => {
 const isActive = selectedProductTab === tab.id;

 return (
 <button
 key={tab.id}
 onClick={() => {
 if (tab.id !== selectedProductTab) {
 setSelectedProductTab(tab.id);
 setSelectedProduct(null);
 }
 }}
 className={`
 whitespace-nowrap px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200
 ${isActive
 ? 'bg-accent-500 text-white border-accent-500 shadow-sm'
 : 'bg-cream-100 text-ink-700 border-ink-200 hover:border-ink-300'
 }
 `}
 >
 {tab.label}
 </button>
 );
 })}
 </div>
 </div>

 {/* 周期选择 */}
 <div className="mb-6">
 <h3 className="text-base font-bold text-ink-900 mb-3">
 选择套餐周期
 </h3>

 <div className="flex gap-2 overflow-x-auto pb-1">
 {PERIOD_TABS.map((tab) => {
 const isActive = selectedSubTab === tab.id;

 return (
 <button
 key={tab.id}
 onClick={() => setSelectedSubTab(tab.id)}
 className={`
 whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200
 ${isActive
 ? 'bg-accent-500 text-white border-accent-500 shadow-sm'
 : 'bg-cream-100 text-ink-700 border-ink-200 hover:border-ink-300'
 }
 `}
 >
 {isActive && <Check className="w-3.5 h-3.5" />}
 <span>{tab.label}</span>
 {tab.days && (
 <>
 <span className={isActive ? 'text-white/70' : 'text-ink-400'}>·</span>
 <span className={isActive ? 'text-white/80' : 'text-ink-500'}>
 {tab.days}天
 </span>
 </>
 )}
 {tab.promoLabel && (
 <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
 isActive
 ? 'bg-white text-accent-600'
 : 'bg-red-500 text-white'
 }`}>
 {tab.promoLabel}
 </span>
 )}
 </button>
 );
 })}
 </div>

 <div className="mt-3 px-1 space-y-2">
 <p className="text-sm text-ink-500">
 {activePeriodTab.description}
 </p>
 {selectedProductTab === 'gpt' && gptPricingHint && (
 <div className="rounded-r-xl border border-accent-500 border-l-4 bg-accent-50/60 px-3 py-2">
 <p className="text-sm leading-6 text-ink-700">
 {gptPricingHint}
 </p>
 </div>
 )}
 </div>
 </div>

 {/* 单独停售公告 */}
 {activeSaleNotice ? (
 <div className="rounded-r-xl border border-accent-500 border-l-4 bg-accent-50/60 p-6 shadow-sm">
 <div className="flex items-start gap-3">
 <div className="w-12 h-12 rounded-2xl bg-accent-100 text-accent-600 flex items-center justify-center text-xl flex-shrink-0">
 公告
 </div>
 <div className="flex-1">
 <h4 className="text-xl font-bold text-ink-900 mb-3">
 {activeSaleNotice.name} 服务暂不支持单独购买
 </h4>
 <p className="text-sm text-ink-600 leading-7 mb-4">
 {activeSaleNotice.reason}
 {' '}如需使用该服务，请购买 VIP大会员套餐。
 </p>
 <div className="mb-5 rounded-2xl border border-accent-200 bg-white px-4 py-3">
 <p className="text-sm text-ink-700 leading-6">
 VIP大会员购买后可使用 AI车队中的 GPT Plus/Business号、Claude Max号、Gemini Pro号和 Grok Super号。
 </p>
 </div>
 <button
 onClick={() => setSelectedProductTab('vip')}
 className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink-900 text-white text-sm font-medium hover:bg-ink-800 transition-all duration-200"
 >
 前往购买 VIP大会员
 <span aria-hidden="true">→</span>
 </button>
 </div>
 </div>
 </div>
 ) : (
 <div className="grid grid-cols-1 gap-4">
 {currentProducts.map((product, index) => {
 // VIP大会员（新版）使用特殊卡片设计
 if (product.productCategoryId === 'aimonkey_vip') {
 return (
 <VIPProductCard
 key={`${product.productCategoryId}-${product.id}`}
 product={product}
 index={index}
 isSelected={selectedProduct?.id === product.id}
 onSelect={() => handleSelectProduct(product)}
 />
 );
 }
 // VIP大会员（经典版/老版）使用经典卡片设计
 if (product.productCategoryId === 'aimonkey_vip_legacy') {
 return (
 <VIPLegacyProductCard
 key={`${product.productCategoryId}-${product.id}`}
 product={product}
 index={index}
 isSelected={selectedProduct?.id === product.id}
 onSelect={() => handleSelectProduct(product)}
 />
 );
 }
 // 其他产品使用标准卡片
 return (
 <ProductCard
 key={`${product.productCategoryId}-${product.id}`}
 product={product}
 index={index}
 isSelected={selectedProduct?.id === product.id}
 onSelect={() => handleSelectProduct(product)}
 />
 );
 })}
 </div>
 )}

 {/* 如果当前分类没有产品 - 联系客服/即将推出提示 */}
 {currentProducts.length === 0 && !activeSaleNotice && (
 <div className="py-16 text-center">
 <div className="w-32 h-32 mx-auto mb-6 bg-accent-100 rounded-xl flex items-center justify-center shadow-xl border-2 border-accent-200">
 <span className="text-6xl animate-bounce">🚀</span>
 </div>
 <h3 className="text-2xl font-bold text-ink-800 mb-3">暂无可购买套餐</h3>
 <p className="text-ink-500 text-base mb-6 max-w-md mx-auto">
 当前分类下暂无可购买套餐，请切换上方产品标签或套餐周期查看
 </p>
 <div className="inline-flex items-center gap-2 px-6 py-3 bg-accent-50 rounded-xl border-2 border-accent-200">
 <span className="text-accent-600 font-semibold">💡 提示</span>
 <span className="text-accent-700 text-sm">您可以切换 VIP、GPT、Grok、Claude 或 Gemini 查看</span>
 </div>
 </div>
 )}

 </div>

 {/* 右侧 - 支付面板 (占2/5) */}
 <div className="lg:col-span-2">
 <div id="checkout-panel" className="lg:sticky lg:top-24">
 <CheckoutPanel
 selectedProduct={selectedProduct}
 customerEmail={customerEmail}
 setCustomerEmail={setCustomerEmail}
 emailError={emailError}
 setEmailError={setEmailError}
 selectedPaymentMethod={selectedPaymentMethod}
 setSelectedPaymentMethod={setSelectedPaymentMethod}
 paymentMethods={paymentMethods}
 paymentLoading={paymentLoading}
 handlePayment={handlePayment}
 showPaymentMethodAlert={showPaymentMethodAlert}
 setShowPaymentMethodAlert={setShowPaymentMethodAlert}
 />
 </div>
 </div>

 </div>
 </div>

 {/* 手动跳转提醒框 */}
 {showManualRedirect && paymentUrl && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-6 transform relative">
 <button
 onClick={() => setShowManualRedirect(false)}
 className="absolute top-4 right-4 w-8 h-8 bg-cream-100 hover:bg-ink-100 rounded-full flex items-center justify-center transition-all duration-200 group"
 >
 <X className="w-4 h-4 text-ink-500 group-hover:text-ink-700" />
 </button>

 <div className="text-center">
 <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
 <Receipt className="w-8 h-8 text-accent-500" />
 </div>

 <h3 className="text-xl font-bold text-ink-900 mb-2">
 💳 支付页面已打开
 </h3>

 <p className="text-ink-600 text-sm mb-6">
 如果支付页面没有自动打开，请点击下方按钮手动打开
 </p>

 {isPolling && (
 <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
 <div className="flex items-center justify-center gap-2">
 <div className="w-4 h-4 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
 <span className="text-accent-700 text-sm font-medium">
 正在检测支付状态...
 </span>
 </div>
 <p className="text-accent-600 text-xs mt-1 text-center">
 支付完成后将自动跳转
 </p>
 </div>
 )}

 <div className="space-y-3">
 <button
 onClick={() => window.location.href = paymentUrl}
 className="w-full bg-accent-500 hover:bg-accent-600 text-white py-4 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-3 shadow-lg transform"
 >
 <Receipt className="w-5 h-5" />
 手动打开支付页面
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* 登录弹窗 */}
 <LoginModal
 isOpen={isLoginModalOpen}
 onClose={() => setIsLoginModalOpen(false)}
 onLogin={() => setIsLoginModalOpen(false)}
 />
 </div>
 );
};

// VIP大会员专属产品卡片组件 - 简约尊贵设计
const VIPProductCard = ({ product, index, isSelected, onSelect }) => {
 // 4个车队的配置信息（不含 Sora）
 const fleets = [
 { name: 'GPT Plus', icon: '🤖', image: '/images/gpt-main.jpg', color: 'from-ink-500 to-ink-600' },
 { name: 'Claude', icon: '🧠', image: '/images/claude-main.jpg', color: 'from-accent-500 to-accent-600' },
 { name: 'Grok', icon: '🚀', image: '/images/grok-main.jpg', color: 'from-accent-500 to-accent-600' },
 { name: 'Gemini', icon: '✨', image: '/images/gemini-main.jpg', color: 'from-green-500 to-emerald-600' }
 ];
 const productDescription = getProductCardDescription(product);

 return (
 <div
 onClick={onSelect}
 className={`
 relative bg-cream-50 rounded-2xl overflow-hidden
 shadow-xl hover:shadow-md transition-all duration-300 cursor-pointer
 border-2 ${isSelected ? 'border-accent-500 ring-4 ring-accent-200' : 'border-ink-200'}
 transform hover:-translate-y-1
 ${product.stock > 0 ? '' : 'opacity-60 cursor-not-allowed'}
 group
 `}
 style={{
 animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
 boxShadow: '0 4px 20px rgba(251, 191, 36, 0.3)' // 金色阴影
 }}
 >
 {/* 金色光泽动画效果 - 增强版 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40"
 style={{
 backgroundSize: '200% 100%',
 animation: 'shimmer 2.5s ease-in-out infinite',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 第二层光泽 - 增加深度 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-cream-100 to-transparent opacity-30"
 style={{
 backgroundSize: '150% 100%',
 animation: 'shimmer 3s ease-in-out infinite 0.5s',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 鼠标悬停时的额外反光效果 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-300"
 style={{
 backgroundSize: '200% 100%',
 animation: 'shimmer-fast 1.5s ease-in-out infinite',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 套餐周期标签 - 左上角 */}
 {product.duration && (
 <div className="absolute top-3 left-3 z-10">
 <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500 rounded-lg shadow-md text-xs font-bold text-white">
 {product.duration}
 </span>
 </div>
 )}

 {/* 选中标记 */}
 {isSelected && (
 <div className="absolute top-3 right-3 z-10">
 <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
 <Check className="w-5 h-5 text-white" />
 </div>
 </div>
 )}

 <div className="p-5 relative z-10">
 {/* VIP标识 - 紧凑设计 */}
 <div className="mb-3 text-center">
 <div className="inline-flex items-center gap-1 px-3 py-1 bg-accent-500 rounded-lg shadow-md">
 <span className="text-lg">👑</span>
 <span className="font-bold text-white text-sm tracking-wide">VIP大会员</span>
 </div>
 </div>

 {/* 4个车队图标 - 横向紧凑排列 */}
 <div className="mb-4 flex justify-center gap-1">
 {fleets.map((fleet, idx) => (
 <div key={idx} className="relative">
 <div className={`w-10 h-10 bg-gradient-to-br ${fleet.color} rounded-lg overflow-hidden shadow-sm border border-white/50`}>
 <img
 src={fleet.image}
 alt={fleet.name}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div className="w-full h-full hidden items-center justify-center text-sm">
 {fleet.icon}
 </div>
 </div>
 {/* 小对勾 */}
 <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border border-white">
 <Check className="w-2 h-2 text-white" />
 </div>
 </div>
 ))}
 </div>

 {/* 产品描述 */}
 <div className="text-center mb-4">
 <div className="min-h-[44px] px-3 py-2 bg-white/70 rounded-xl border border-white/80">
 <p className="text-xs text-ink-600 leading-5">
 {productDescription}
 </p>
 </div>
 </div>

 {/* 价格 - 单行显示 */}
 <div className="text-center">
 <div className="inline-flex items-baseline gap-2">
 <span
 className="text-3xl font-black text-accent-600"
 style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
 >
 ¥{formatPrice(getProductRmbPrice(product))}
 </span>
 <span className="text-sm font-semibold text-ink-600">
 约 ${formatPrice(product.price)}
 </span>
 </div>
 </div>
 </div>
 </div>
 );
};

// VIP大会员（经典版）专属产品卡片组件 - 老会员专享，3个车队，银色主题
const VIPLegacyProductCard = ({ product, index, isSelected, onSelect }) => {
 // 3个车队的配置信息（不含 Sora / Claude，包含 Gemini）
 const fleets = [
 { name: 'GPT Plus', icon: '🤖', image: '/images/gpt-main.jpg', color: 'from-ink-500 to-ink-600' },
 { name: 'Grok', icon: '🚀', image: '/images/grok-main.jpg', color: 'from-accent-500 to-accent-600' },
 { name: 'Gemini', icon: '✨', image: '/images/gemini-main.jpg', color: 'from-green-500 to-emerald-600' }
 ];
 const productDescription = getProductCardDescription(product);

 return (
 <div
 onClick={onSelect}
 className={`
 relative bg-cream-50 rounded-2xl overflow-hidden
 shadow-xl hover:shadow-md transition-all duration-300 cursor-pointer
 border-2 ${isSelected ? 'border-accent-500 ring-4 ring-accent-200' : 'border-accent-300'}
 transform hover:-translate-y-1
 ${product.stock > 0 ? '' : 'opacity-60 cursor-not-allowed'}
 group
 `}
 style={{
 animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
 boxShadow: '0 4px 20px rgba(167, 139, 250, 0.3)' // 紫色阴影
 }}
 >
 {/* 紫色光泽动画效果 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
 style={{
 backgroundSize: '200% 100%',
 animation: 'shimmer 2.5s ease-in-out infinite',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 第二层光泽 - 紫色 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-cream-100 to-transparent opacity-20"
 style={{
 backgroundSize: '150% 100%',
 animation: 'shimmer 3s ease-in-out infinite 0.5s',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 鼠标悬停时的额外反光效果 */}
 <div
 className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-300"
 style={{
 backgroundSize: '200% 100%',
 animation: 'shimmer-fast 1.5s ease-in-out infinite',
 transform: 'skewX(-20deg)'
 }}
 ></div>

 {/* 套餐周期标签 - 左上角 */}
 {product.duration && (
 <div className="absolute top-3 left-3 z-10">
 <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500 rounded-lg shadow-md text-xs font-bold text-white">
 {product.duration}
 </span>
 </div>
 )}

 {/* 选中标记 */}
 {isSelected && (
 <div className="absolute top-3 right-3 z-10">
 <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
 <Check className="w-5 h-5 text-white" />
 </div>
 </div>
 )}

 <div className="p-5 relative z-10">
 {/* VIP标识 + 老会员专享标签 - 紫色主题 */}
 <div className="mb-3 text-center">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-500 rounded-lg shadow-md">
 <span className="text-lg">👑</span>
 <span className="font-bold text-white text-sm tracking-wide">VIP大会员（老用户专享）</span>
 </div>
 </div>

 {/* 3个车队图标 - 横向紧凑排列 */}
 <div className="mb-4 flex justify-center gap-1.5">
 {fleets.map((fleet, idx) => (
 <div key={idx} className="relative">
 <div className={`w-11 h-11 bg-gradient-to-br ${fleet.color} rounded-lg overflow-hidden shadow-sm border border-white/50`}>
 <img
 src={fleet.image}
 alt={fleet.name}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div className="w-full h-full hidden items-center justify-center text-sm">
 {fleet.icon}
 </div>
 </div>
 {/* 小对勾 */}
 <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border border-white">
 <Check className="w-2 h-2 text-white" />
 </div>
 </div>
 ))}
 </div>

 {/* 老会员版本说明 */}
 <div className="mb-3 text-center">
 <div className="px-3 py-2 bg-red-50 rounded-xl border border-red-200 text-left">
 <p className="text-xs leading-5 text-red-700">
 因 Claude 当前并发与售后成本持续上升，为保障相关服务的整体体验，
 老会员专享优惠套餐现已调整为不含 Claude，并补充加入 Gemini。
 购买后可使用 GPT Plus/Business、Grok Super 和 Gemini Pro。感谢老用户一直以来的理解与支持，
 我们也会继续以更实惠的价格，为大家提供稳定服务。
 </p>
 </div>
 </div>

 {/* 产品描述 */}
 <div className="text-center mb-4">
 <div className="min-h-[44px] px-3 py-2 bg-white/70 rounded-xl border border-white/80">
 <p className="text-xs text-ink-600 leading-5">
 {productDescription}
 </p>
 </div>
 </div>

 {/* 价格 - 单行显示 */}
 <div className="text-center">
 <div className="inline-flex items-baseline gap-2">
 <span
 className="text-3xl font-black text-accent-600"
 style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
 >
 ¥{formatPrice(getProductRmbPrice(product))}
 </span>
 <span className="text-sm font-semibold text-ink-600">
 约 ${formatPrice(product.price)}
 </span>
 </div>
 </div>
 </div>
 </div>
 );
};

// 产品卡片组件
const ProductCard = ({ product, index, isSelected, onSelect }) => {
 const theme = getCategoryTheme(product.productCategoryId);
 // 优先使用产品数据中的图片，确保车队产品显示正确的图片
 const productImage = product.productImage || getProductMainImage(product.productCategoryId);
 const isGift = product.isGift || false;
 const productDescription = getProductCardDescription(product);

 return (
 <div
 onClick={isGift ? null : onSelect} // 赠品不可选择
 className={`
 relative bg-gradient-to-br ${theme.gradient} rounded-2xl overflow-hidden
 shadow-lg hover:shadow-md transition-all duration-300
 border-2 ${isSelected ? 'border-accent-500 ring-4 ring-accent-200' : theme.border}
 transform hover:-translate-y-1
 ${product.stock > 0 && !isGift ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
 `}
 style={{
 animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`
 }}
 >
 {/* 套餐周期标签 - 左上角 */}
 {!isGift && product.duration && (
 <div className="absolute top-3 left-3 z-10">
 <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-ink-900 rounded-lg shadow-md text-xs font-bold text-white">
 {product.duration}
 </span>
 </div>
 )}

 {/* 赠品标签 */}
 {isGift && (
 <div className="absolute top-3 left-3 z-10">
 <div className="px-3 py-1.5 bg-green-500 rounded-lg shadow-lg">
 <span className="text-white text-xs font-bold">🎁 {product.giftLabel}</span>
 </div>
 </div>
 )}

 {/* 选中标记（非赠品） */}
 {isSelected && !isGift && (
 <div className="absolute top-3 right-3 z-10">
 <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
 <Check className="w-5 h-5 text-white" />
 </div>
 </div>
 )}

 <div className="p-5">
 {/* 产品图片 */}
 <div className="mb-4 flex justify-center">
 <div className="w-16 h-16 bg-white rounded-xl overflow-hidden shadow-md">
 <img
 src={productImage}
 alt={product.productName}
 className="w-full h-full object-cover"
 />
 </div>
 </div>

 {/* 产品信息 */}
 <div className="text-center mb-4">
 <h4 className="font-bold text-ink-900 mb-2 text-base">
 {product.productName}
 </h4>
 <div className="min-h-[44px] px-3 py-2 bg-white/80 rounded-xl border border-white/90">
 <p className="text-xs text-ink-600 leading-5">
 {productDescription}
 </p>
 </div>
 </div>

 {/* 价格或赠品提示 */}
 <div className="text-center">
 {isGift ? (
 <div className="inline-flex items-center gap-2 px-3 py-2 bg-accent-100 rounded-lg border border-accent-200">
 <span className="text-sm font-bold text-accent-700">免费赠送</span>
 </div>
 ) : (
 <div className="inline-flex items-baseline gap-2">
 <span
 className="text-3xl font-black text-accent-600"
 style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
 >
 ¥{formatPrice(getProductRmbPrice(product))}
 </span>
 <span className="text-sm font-semibold text-ink-600">
 约 ${formatPrice(product.price)}
 </span>
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

// 右侧支付面板组件
const CheckoutPanel = ({
 selectedProduct,
 customerEmail,
 setCustomerEmail,
 emailError,
 setEmailError,
 selectedPaymentMethod,
 setSelectedPaymentMethod,
 paymentMethods,
 paymentLoading,
 handlePayment,
 showPaymentMethodAlert,
 setShowPaymentMethodAlert
}) => {
 const totalUsd = selectedProduct ? Number(selectedProduct.price) : 0;
 const totalRmb = selectedProduct ? getProductRmbPrice(selectedProduct) : 0;

 return (
 <div className="bg-white rounded-xl shadow-md border border-accent-100 overflow-hidden">
 <div className="p-6 space-y-5">

 {/* 标题 */}
 <div className="flex items-center gap-3 pb-4 border-b border-ink-200">
 <div className="w-10 h-10 bg-accent-500 rounded-xl flex items-center justify-center">
 <Receipt className="w-5 h-5 text-white" />
 </div>
 <h2 className="text-xl font-bold text-ink-900">确认订单</h2>
 </div>

 {/* 选中的产品 */}
 {selectedProduct ? (
 <div className="p-4 bg-accent-50 rounded-2xl border border-accent-200">
 <div className="flex items-start gap-3">
 <div className="w-12 h-12 bg-white rounded-lg overflow-hidden shadow-sm flex-shrink-0">
 <img
 src={getProductMainImage(selectedProduct.productCategoryId)}
 alt={selectedProduct.productName}
 className="w-full h-full object-cover"
 />
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="font-bold text-ink-900 text-sm mb-1">
 {selectedProduct.productName}
 </h4>
 <p className="text-accent-600 text-xs font-semibold">
 {selectedProduct.label || selectedProduct.key}
 </p>
 {/* 套餐信息 */}
 {selectedProduct.month > 0 && (
 <div className="mt-1 text-xs text-ink-600">
 📅 {selectedProduct.month}个月套餐 · {selectedProduct.duration || `${selectedProduct.month}个月`}
 </div>
 )}
 <div className="mt-2 flex items-baseline gap-2">
 <span
 className="text-2xl font-black text-accent-600"
 style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
 >
 ¥{formatPrice(getProductRmbPrice(selectedProduct))}
 </span>
 <span className="text-sm font-semibold text-ink-600">
 约 ${formatPrice(selectedProduct.price)}
 </span>
 </div>
 {/* 钻石奖励 */}
 {selectedProduct.diamondReward > 0 && (
 <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-accent-100 rounded-lg border border-accent-200">
 <span className="text-xs">🎁</span>
 <span className="text-xs font-bold text-accent-700">
 +{selectedProduct.diamondReward} 钻石
 </span>
 </div>
 )}
 </div>
 </div>
 </div>
 ) : (
 <div className="p-8 text-center text-ink-400">
 <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p className="text-sm">请先选择左侧产品</p>
 </div>
 )}

 {/* 产品说明 */}
 <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
 <div className="flex items-start gap-3">
 <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-sky-200 bg-white text-sky-600">
 <Info className="h-4 w-4" aria-hidden="true" />
 </div>
 <div className="min-w-0">
 <h3 className="text-sm font-bold text-ink-900">购买前请了解</h3>
 <p className="mt-1 text-xs leading-5 text-ink-700">
 本产品为 AI 车队账号网页共享服务，<strong className="font-bold text-green-700">对话记录仅自己可见</strong>。
 </p>
 <p className="text-xs leading-5 text-ink-600">
 数字产品购买后不退不换，请确认所选套餐及周期无误。
 </p>
 </div>
 </div>
 </div>

 {/* 收货信息（只读，显示用户名） */}
 <div>
 <label className="block text-sm font-semibold text-ink-900 mb-2 flex items-center gap-2">
 <Mail className="w-4 h-4 text-accent-500" />
 收货信息
 </label>
 <input
 type="text"
 value={customerEmail}
 readOnly
 placeholder="请先登录"
 className={`w-full px-4 py-3 border-2 ${
 emailError ? 'border-red-300 bg-red-50' : 'border-ink-200 bg-cream-100'
 } rounded-xl text-ink-700 cursor-not-allowed`}
 />
 {emailError ? (
 <p className="mt-1 text-xs text-red-500">{emailError}</p>
 ) : (
 <p className="mt-1 text-xs text-ink-500">使用您的账号信息（邮箱或手机号）</p>
 )}
 </div>

 {/* 支付方式选择 */}
 <div>
 <label className="block text-sm font-semibold text-ink-900 mb-3">
 选择支付渠道
 </label>
 <div className="space-y-2">
 {paymentMethods.all.map((method) => (
 <button
 key={method.id}
 onClick={() => setSelectedPaymentMethod(method.id)}
 className={`w-full p-3 rounded-xl text-left transition-all duration-200 flex items-center justify-between ${
 selectedPaymentMethod === method.id
 ? 'border-2 border-accent-500 bg-accent-50 shadow-md'
 : 'border border-ink-200 hover:border-accent-300 bg-white'
 }`}
 >
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 flex-shrink-0">
 <img
 src={method.icon}
 alt={method.name}
 className="w-full h-full object-contain rounded"
 />
 </div>
 <span className="font-semibold text-ink-900 text-sm">{method.name}</span>
 </div>
 {selectedPaymentMethod === method.id && (
 <div className="w-5 h-5 bg-accent-500 rounded-full flex items-center justify-center">
 <Check className="w-3 h-3 text-white" />
 </div>
 )}
 </button>
 ))}
 </div>
 </div>

 {/* 总计 */}
 <div className="p-4 bg-accent-100 rounded-2xl border border-accent-200">
 <div className="flex justify-between items-center">
 <span className="text-lg font-bold text-ink-900">总计</span>
 <div className="text-right">
 <div className="text-2xl font-bold text-accent-600">
 ¥{formatPrice(totalRmb)}
 </div>
 <div className="mt-1 text-sm font-semibold text-ink-600">
 约 ${formatPrice(totalUsd)}
 </div>
 </div>
 </div>
 </div>

 {/* 支付按钮 */}
 <button
 onClick={handlePayment}
 disabled={paymentLoading || !selectedProduct}
 className={`w-full py-4 rounded-2xl font-bold text-lg shadow-md flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden group relative ${
 !paymentLoading && selectedProduct
 ? 'bg-accent-500 hover:bg-accent-600 text-white transform'
 : 'bg-ink-100 text-ink-400 cursor-not-allowed'
 }`}
 >
 {/* 动态背景效果 */}
 {!paymentLoading && selectedProduct && (
 <div className="absolute inset-0 bg-accent-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
 )}

 {/* 按钮内容 */}
 <div className="relative z-10 flex items-center gap-3">
 {paymentLoading ? (
 <>
 <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
 <span>处理中...</span>
 </>
 ) : (
 <>
 <CreditCard className="w-6 h-6" />
 <span>立即支付 ¥{formatPrice(totalRmb)}</span>
 <span className="text-2xl">→</span>
 </>
 )}
 </div>
 </button>

 {/* 支付方式选择提醒 */}
 {showPaymentMethodAlert && (
 <div className="p-3 bg-red-50 border-2 border-red-200 rounded-xl animate-pulse">
 <div className="flex items-center gap-2">
 <span className="text-red-500 text-lg">⚠️</span>
 <div className="flex-1">
 <p className="font-bold text-red-800 text-xs">
 请选择支付方式
 </p>
 </div>
 <button
 onClick={() => setShowPaymentMethodAlert(false)}
 className="w-5 h-5 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center"
 >
 <X className="w-3 h-3 text-red-500" />
 </button>
 </div>
 </div>
 )}

 {/* 安全提示 */}
 <div className="flex items-center justify-center gap-3 text-xs text-ink-600 pt-3 border-t border-ink-100">
 <div className="flex items-center gap-1">
 <Shield className="w-3 h-3 text-green-500" />
 <span>安全支付</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-1">
 <Lock className="w-3 h-3 text-accent-500" />
 <span>SSL加密</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-1">
 <RefreshCw className="w-3 h-3 text-accent-500" />
 <span>质量保障</span>
 </div>
 </div>

 </div>
 </div>
 );
};

export default ShopCheckout;

// 添加动画样式
if (typeof document !== 'undefined') {
 const style = document.createElement('style');
 style.textContent = `
 @keyframes fadeInUp {
 from {
 opacity: 0;
 transform: translateY(20px) scale(0.98);
 }
 to {
 opacity: 1;
 transform: translateY(0) scale(1);
 }
 }

 @keyframes float {
 0%, 100% { transform: translateY(0px); }
 50% { transform: translateY(-3px); }
 }

 @keyframes shimmer {
 0% {
 background-position: -200% 0;
 }
 100% {
 background-position: 200% 0;
 }
 }

 .animate-float {
 animation: float 3s ease-in-out infinite;
 }

 .animate-shimmer {
 animation: shimmer 3s ease-in-out infinite;
 }
 `;
 if (!document.head.querySelector('style[data-shop-checkout-animations]')) {
 style.setAttribute('data-shop-checkout-animations', 'true');
 document.head.appendChild(style);
 }
}
